import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';

const SEARCH_DELAY_MS = 300;

function categoryId(category) {
  return String(category?.id ?? category?.category_id ?? '');
}

function categoryLabel(category, localizeMessage) {
  return category?.name || localizeMessage('search.page.dataSettings.category.fallbackName', {
    id: categoryId(category),
  });
}

/**
 * Merchant-friendly category selector backed by the existing empty-query
 * category lookup actions. It deliberately hides platform category IDs from
 * the primary interaction while retaining them in secondary option text.
 */
export default function CategoryExclusionPicker({
  value,
  onChange,
  onSearch,
  disabled,
  error,
}) {
  const localizeMessage = useLocalizedMessage();
  const timerRef = useRef(null);
  const requestRef = useRef(0);
  const [options, setOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const mergedOptions = useMemo(() => {
    const byId = new Map();
    [...value, ...options].forEach((category) => {
      byId.set(categoryId(category), category);
    });
    return Array.from(byId.values());
  }, [options, value]);

  const handleInputChange = (_event, query, reason) => {
    if (reason !== 'input') return;
    window.clearTimeout(timerRef.current);
    setSearchError('');

    const trimmed = query.trim();
    if (!trimmed) {
      setOptions([]);
      setIsSearching(false);
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    timerRef.current = window.setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await onSearch(trimmed);
        if (requestRef.current === requestId) {
          setOptions(Array.isArray(result) ? result : []);
        }
      } catch (searchFailure) {
        if (requestRef.current === requestId) {
          setOptions([]);
          setSearchError(
            searchFailure?.message
              || localizeMessage('search.page.dataSettings.category.searchError'),
          );
        }
      } finally {
        if (requestRef.current === requestId) setIsSearching(false);
      }
    }, SEARCH_DELAY_MS);
  };

  return (
    <Box mt={2} data-testid="category-exclusion-picker">
      <Typography variant="subtitle2" gutterBottom>
        {localizeMessage('search.page.dataSettings.category.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {localizeMessage('search.page.dataSettings.category.description')}
      </Typography>
      <Box mt={1.5}>
        <Autocomplete
          multiple
          filterSelectedOptions
          filterOptions={(availableOptions) => availableOptions}
          options={mergedOptions}
          value={value}
          disabled={disabled}
          loading={isSearching}
          getOptionLabel={(category) => categoryLabel(category, localizeMessage)}
          getOptionSelected={(option, selected) => categoryId(option) === categoryId(selected)}
          onInputChange={handleInputChange}
          onChange={(_event, categories) => onChange(categories)}
          noOptionsText={localizeMessage('search.page.dataSettings.category.noOptions')}
          loadingText={localizeMessage('search.page.dataSettings.category.searching')}
          renderTags={(selected, getTagProps) => selected.map((category, index) => (
            <Chip
              {...getTagProps({ index })}
              key={categoryId(category)}
              label={categoryLabel(category, localizeMessage)}
              size="small"
            />
          ))}
          renderOption={(category) => (
            <Box minWidth={0}>
              <Typography variant="body2">
                {categoryLabel(category, localizeMessage)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {category.path ? `${category.path} · ` : ''}
                {localizeMessage('search.page.dataSettings.category.id', {
                  id: categoryId(category),
                })}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              label={localizeMessage('search.page.dataSettings.category.searchLabel')}
              placeholder={value.length
                ? ''
                : localizeMessage('search.page.dataSettings.category.searchPlaceholder')}
              error={Boolean(error || searchError)}
              helperText={error || searchError
                || localizeMessage('search.page.dataSettings.category.helper')}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'category-search-input',
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
}

CategoryExclusionPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    category_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    path: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
};

CategoryExclusionPicker.defaultProps = {
  value: [],
  disabled: false,
  error: '',
};
